import { useMemo } from 'react';
import { useGameState } from '../GameProvider';
import './ScoreHistory.css';

/**
 * In-game running score table: one row per completed round (clickable to
 * replay that round's showdown) plus a running-tally footer. Reads
 * roundHistory (written per round by the scoreRound CF) from game state.
 */
export default function ScoreHistory({ onSelectRound }) {
  const { roundHistory, players, sessionTotals, uid } = useGameState();

  // Most-recent round first (descending), so the newest hand sits at the top.
  const rounds = useMemo(() =>
    Object.values(roundHistory || {})
      .filter(Boolean)
      .sort((a, b) => (b.roundNumber || 0) - (a.roundNumber || 0)),
  [roundHistory]);

  // Column order: everyone who appears in any round or is currently seated.
  const columns = useMemo(() => {
    const seen = new Map();
    for (const r of rounds) {
      for (const [id, name] of Object.entries(r.names || {})) {
        if (!seen.has(id)) seen.set(id, name);
      }
    }
    for (const [id, p] of Object.entries(players || {})) {
      if (!p.kicked && !seen.has(id)) seen.set(id, p.displayName || 'Player');
    }
    // Put me first for quick self-reference.
    const ids = [...seen.keys()];
    ids.sort((a, b) => (a === uid ? -1 : b === uid ? 1 : 0));
    return ids.map(id => ({ id, name: seen.get(id) }));
  }, [rounds, players, uid]);

  if (rounds.length === 0) return null;

  const fmt = (n) => (n > 0 ? `+${n}` : `${n ?? 0}`);
  const cls = (n) => (n > 0 ? 'sh-pos' : n < 0 ? 'sh-neg' : 'sh-zero');

  return (
    <div className="score-history" data-testid="score-history">
      <div className="score-history__title">Score History <span>· tap a round to review</span></div>
      <div className="score-history__scroll">
        <table className="score-history__table">
          <thead>
            {/* Player names */}
            <tr>
              <th className="score-history__corner">Rd</th>
              {columns.map(c => (
                <th key={c.id} className={c.id === uid ? 'score-history__me' : ''}>{c.name}</th>
              ))}
            </tr>
            {/* Cumulative total pinned at the top */}
            <tr className="score-history__total-row">
              <th className="score-history__rd">Tot</th>
              {columns.map(c => {
                const v = sessionTotals?.[c.id] || 0;
                return <th key={c.id} className={`score-history__total ${cls(v)}`}>{fmt(v)}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {/* Rounds, newest first */}
            {rounds.map(r => (
              <tr
                key={r.roundNumber}
                className="score-history__row"
                data-testid={`score-history-round-${r.roundNumber}`}
                onClick={() => onSelectRound?.(r)}
              >
                <td className="score-history__rd">R{r.roundNumber}</td>
                {columns.map(c => {
                  const v = r.totals?.[c.id];
                  return (
                    <td key={c.id} className={v === undefined ? 'sh-zero' : cls(v)}>
                      {v === undefined ? '—' : fmt(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
